import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Archive,
  Banknote,
  CheckCircle2,
  CircleDashed,
  Cylinder,
  Loader2,
  Plus,
  RotateCcw,
  Search,
  Truck,
  Undo2,
  Wrench,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useCustomerDetailContext } from '@/pages/admin/AdminCustomerDetail';
import {
  searchClosureCatalog,
  useAccountClosure,
  useClosureAction,
  type CatalogPickerItem,
  type ClosureGates,
  type ClosureRow,
  type ClosureSubject,
} from '@/hooks/admin/useAccountClosure';

/**
 * ClosureTab — the account-closure status page (superadmin).
 *
 * Guided checklist per Sky's closure rules: equipment removed → tanks
 * resolved (pickup scheduled or closure audit) → balances cleared/written
 * off → refund question answered (return order pushed to SF as a negative
 * order) → superadmin closes the account. Every gate is re-validated
 * server-side at Close, so this page is a status view, not the enforcement.
 */

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** Portal customer-detail tab: the closure panel for the customer being viewed. */
export default function ClosureTab() {
  const { customer, refetch } = useCustomerDetailContext();
  return <ClosurePanel subject={{ customerId: customer.id }} name={customer.name} onChanged={refetch} />;
}

/**
 * The closure workflow itself. Keyed on a portal uuid OR a QuickBooks id, so
 * the same four checks run for a customer who was never on the portal
 * (/admin/customers/qbo/:qboId/closure).
 */
export function ClosurePanel({
  subject,
  name,
  onChanged,
}: {
  subject: ClosureSubject;
  name: string;
  onChanged?: () => void;
}) {
  const refetch = () => onChanged?.();
  const customer = { id: subject.customerId ?? null, name };
  const { data, isLoading, error } = useAccountClosure(subject);
  const act = useClosureAction(subject);
  const [notice, setNotice] = useState<string | null>(null);

  const run = (input: Parameters<typeof act.mutate>[0], onDone?: () => void) => {
    setNotice(null);
    act.mutate(input, {
      onSuccess: () => {
        onDone?.();
        refetch();
      },
      onError: (e) => setNotice(e instanceof Error ? e.message : 'Action failed'),
    });
  };

  if (isLoading || !data) {
    return (
      <div className="px-1 py-10 text-center text-sm text-brix-muted">
        <Loader2 size={16} className="animate-spin inline-block mr-2" />
        Loading closure status…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border border-brix-warning/30 bg-brix-warning/5 px-3 py-2 text-sm text-brix-warning">
        {error instanceof Error ? error.message : 'Could not load closure status.'}
      </div>
    );
  }

  const { closure, gates } = data;
  const inProgress = closure?.status === 'in_progress';

  // Already closed through this flow.
  if (closure?.status === 'closed') {
    return (
      <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-6 text-center space-y-2">
        <Archive size={28} className="text-brix-primary mx-auto" />
        <div className="font-display text-lg font-semibold text-brix-ink">Account closed</div>
        <p className="text-sm text-brix-muted">
          Closed by {closure.closed_by ?? 'a superadmin'} on{' '}
          {closure.closed_at ? new Date(closure.closed_at).toLocaleDateString() : '—'}.
          {closure.refund_sf_job_id && <> Return order SF job #{closure.refund_sf_job_id}.</>}
        </p>
        <div className="text-xs space-y-1 max-w-md mx-auto text-left">
          <p className={closure.qbo_deactivated_at ? 'text-emerald-700' : 'text-amber-700'}>
            {closure.qbo_deactivated_at
              ? '✓ Inactivated in QuickBooks.'
              : `⚠ QuickBooks deactivation failed${closure.qbo_deactivate_error ? ` — ${closure.qbo_deactivate_error}` : ''}. Make the customer inactive in QBO by hand.`}
          </p>
          <p className={closure.sf_closeout_job_id ? 'text-emerald-700' : 'text-amber-700'}>
            {closure.sf_closeout_job_id
              ? `✓ Service Fusion close-out ticket #${closure.sf_closeout_job_id} created — ops inactivates the customer in SF and closes it (no invoice).`
              : `⚠ SF close-out ticket failed${closure.sf_deactivate_error ? ` — ${closure.sf_deactivate_error}` : ''}. Inactivate the customer in Service Fusion by hand.`}
          </p>
        </div>
        <p className="text-xs text-brix-muted">
          {subject.customerId
            ? 'Reactivate from the Overview tab if this customer comes back.'
            : 'To bring this customer back, reactivate it in QuickBooks.'}
        </p>
      </div>
    );
  }

  // No closure started yet — intro screen.
  if (!inProgress) {
    return (
      <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-6 space-y-4">
        <div className="flex items-center gap-2.5">
          <Archive size={20} className="text-brix-primary" />
          <h2 className="font-display text-lg font-semibold text-brix-ink">Close this account</h2>
        </div>
        <p className="text-sm text-brix-muted max-w-xl">
          Closing walks through four checks before the account can be deactivated: rented
          equipment removed, tanks picked up or audited, open balances cleared or written off,
          and any customer refunds handled as a return order. Nothing is changed until the final
          step — you can cancel the process at any time.
        </p>
        <p className="text-xs text-brix-muted">
          Just need a temporary hold? Use <strong>Lock ordering</strong> on the Overview tab
          instead — it blocks orders without closing the account.
        </p>
        <Button
          variant="primary"
          size="sm"
          disabled={act.isPending}
          onClick={() => run({ action: 'start' })}
        >
          {act.isPending ? <Loader2 size={12} className="animate-spin" /> : <Archive size={12} />}
          Begin account closure
        </Button>
        {notice && <Notice text={notice} />}
      </div>
    );
  }

  const stepsDone = [gates.equipment.ok, gates.tanks.ok, gates.balances.ok, gates.refunds.ok].filter(Boolean).length;

  return (
    <div className="space-y-4">
      {/* Progress header */}
      <div className="rounded-xl border border-brix-border bg-brix-surface px-5 py-4 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="font-display text-base font-semibold text-brix-ink">
            Account closure in progress
          </div>
          <div className="text-xs text-brix-muted mt-0.5">
            Started {new Date(closure.created_at).toLocaleDateString()} by {closure.created_by ?? '—'} ·{' '}
            {stepsDone} of 4 checks clear
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-36 rounded-full bg-brix-border overflow-hidden">
            <div
              className={cn('h-full transition-all', gates.allClear ? 'bg-emerald-500' : 'bg-brix-primary')}
              style={{ width: `${(stepsDone / 4) * 100}%` }}
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            disabled={act.isPending}
            onClick={() => {
              if (window.confirm('Cancel this closure? The checklist progress is discarded and the account stays active.')) {
                run({ action: 'cancel' });
              }
            }}
          >
            <RotateCcw size={12} /> Cancel closure
          </Button>
        </div>
      </div>

      {notice && <Notice text={notice} />}

      {gates.stores && !gates.stores.ok && (
        <div className="rounded-xl border border-amber-300 bg-amber-50/60 px-5 py-3 text-sm text-amber-900">
          <strong>Close the stores first.</strong> This account has {gates.stores.active.length} active
          store{gates.stores.active.length === 1 ? '' : 's'} in QuickBooks:{' '}
          {gates.stores.active.slice(0, 6).map((s) => (
            <Link key={s.id} to={`/admin/customers/qbo/${s.id}/closure`} className="font-semibold text-brix-primary no-underline mr-2">
              {s.name}
            </Link>
          ))}
          {gates.stores.active.length > 6 && '…'} The parent can be closed once none are active.
        </div>
      )}

      <EquipmentStep gates={gates} closure={closure} busy={act.isPending} run={run} />
      <TanksStep gates={gates} closure={closure} subject={subject} busy={act.isPending} run={run} />
      <BalancesStep gates={gates} closure={closure} busy={act.isPending} run={run} />
      <RefundsStep gates={gates} closure={closure} busy={act.isPending} run={run} />

      {/* Final close */}
      <div
        className={cn(
          'rounded-xl border px-5 py-4 flex items-center justify-between gap-4 flex-wrap',
          gates.allClear ? 'border-emerald-300 bg-emerald-50/50' : 'border-brix-border bg-brix-surface-alt/40',
        )}
      >
        <div>
          <div className="font-display text-sm font-semibold text-brix-ink">Close account</div>
          <div className="text-xs text-brix-muted mt-0.5">
            {gates.allClear
              ? (subject.customerId ? 'All checks are clear. Closing deactivates the account and ends portal access for its users.' : 'All checks are clear. Closing inactivates it in QuickBooks and Service Fusion and removes it from the active customer lists.')
              : 'Disabled until every check above is green. Superadmins only.'}
          </div>
        </div>
        <Button
          variant="primary"
          size="sm"
          className={gates.allClear ? 'bg-red-700 hover:bg-red-800' : ''}
          disabled={!gates.allClear || act.isPending}
          onClick={() => {
            if (
              window.confirm(
                subject.customerId
                  ? `Close ${customer.name}? The account is deactivated and its users lose portal access. This is the final step.`
                  : `Close ${customer.name}? It is inactivated in QuickBooks and a Service Fusion close-out ticket is raised; it drops off the active customer lists. This is the final step.`,
              )
            ) {
              run({ action: 'close' });
            }
          }}
        >
          {act.isPending ? <Loader2 size={12} className="animate-spin" /> : <Archive size={12} />}
          Close account
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------ step frame ------------------------------ */

function Step({
  n,
  title,
  ok,
  icon,
  children,
}: {
  n: number;
  title: string;
  ok: boolean;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('rounded-xl border px-5 py-4', ok ? 'border-emerald-200 bg-emerald-50/40' : 'border-brix-border bg-brix-surface')}>
      <div className="flex items-center gap-2.5 mb-2">
        {ok ? (
          <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />
        ) : (
          <CircleDashed size={18} className="text-amber-500 shrink-0" />
        )}
        <span className="text-[11px] font-bold text-brix-muted">STEP {n}</span>
        <span className="font-display text-sm font-semibold text-brix-ink inline-flex items-center gap-1.5">
          {icon}
          {title}
        </span>
      </div>
      <div className="pl-7 space-y-2.5">{children}</div>
    </div>
  );
}

function Notice({ text }: { text: string }) {
  return (
    <div className="rounded-lg border border-brix-warning/40 bg-brix-warning/5 px-3 py-2 text-sm text-brix-warning">
      {text}
    </div>
  );
}

type RunFn = (input: Parameters<ReturnType<typeof useClosureAction>['mutate']>[0], onDone?: () => void) => void;

/* ------------------------------ step 1: equipment ------------------------------ */

function EquipmentStep({
  gates,
  closure,
  busy,
  run,
}: {
  gates: ClosureGates;
  closure: ClosureRow;
  busy: boolean;
  run: RunFn;
}) {
  const [overriding, setOverriding] = useState(false);
  const [note, setNote] = useState('');
  const g = gates.equipment;

  return (
    <Step n={1} title="Rented equipment removed" ok={g.ok} icon={<Wrench size={13} />}>
      {g.onSiteCount === 0 ? (
        <p className="text-sm text-brix-muted">
          The equipment system (ERLS) shows nothing on site for this customer.
        </p>
      ) : (
        <>
          <p className="text-sm text-brix-ink">
            <strong>{g.onSiteCount}</strong> piece{g.onSiteCount === 1 ? '' : 's'} of equipment still
            show{g.onSiteCount === 1 ? 's' : ''} on site in the ERLS. Schedule removal there first —
            the mirror refreshes nightly.
          </p>
          <ul className="text-xs text-brix-muted space-y-0.5">
            {g.assets.map((a) => (
              <li key={a.id}>
                • {a.catalog_name ?? a.qbo_item_name ?? a.description ?? 'Equipment'}
                {a.asset_tag ? ` · tag ${a.asset_tag}` : ''}
                {a.status ? ` · ${a.status}` : ''}
              </li>
            ))}
          </ul>
          {g.overridden ? (
            <p className="text-xs text-emerald-700">
              Overridden by {closure.equipment_override_by} — {closure.equipment_override_note}
            </p>
          ) : overriding ? (
            <div className="space-y-2">
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Why the ERLS is out of date (e.g. removed yesterday, mirror hasn't synced)"
              />
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy || note.trim().length < 5}
                  onClick={() => run({ action: 'override_equipment', note: note.trim() }, () => setOverriding(false))}
                >
                  Confirm override
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setOverriding(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setOverriding(true)}>
              Equipment already removed — override with a note
            </Button>
          )}
        </>
      )}
    </Step>
  );
}

/* ------------------------------ step 2: tanks ------------------------------ */

function TanksStep({
  gates,
  closure,
  subject,
  busy,
  run,
}: {
  gates: ClosureGates;
  closure: ClosureRow;
  subject: ClosureSubject;
  busy: boolean;
  run: RunFn;
}) {
  const [scheduling, setScheduling] = useState(false);
  const [note, setNote] = useState('');
  const g = gates.tanks;

  return (
    <Step n={2} title="Tanks picked up or audited" ok={g.ok} icon={<Cylinder size={13} />}>
      {g.total === 0 && g.buckets.length === 0 ? (
        <p className="text-sm text-brix-muted">No tanks on the books for this customer.</p>
      ) : (
        <p className="text-sm text-brix-ink">
          <strong>{g.total}</strong> tank{g.total === 1 ? '' : 's'} on site per the books:{' '}
          {g.buckets.map((b) => `${b.label} × ${b.systemCount}`).join(', ')}.
        </p>
      )}

      {g.resolution ? (
        <p className="text-xs text-emerald-700">
          {g.resolution === 'pickup_scheduled' ? 'Pickup scheduled' : 'Account-closure tank audit performed'}
          {closure.tanks_note ? ` — ${closure.tanks_note}` : ''} (by {closure.tanks_resolved_by})
        </p>
      ) : g.total > 0 ? (
        <div className="space-y-2">
          {scheduling ? (
            <div className="space-y-2">
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Pickup date / SF job # (e.g. pickup 7/15, SF job 1092xxxxxx)"
              />
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy || note.trim().length < 3}
                  onClick={() =>
                    run({ action: 'resolve_tanks', resolution: 'pickup_scheduled', note: note.trim() }, () => setScheduling(false))
                  }
                >
                  <Truck size={12} /> Record pickup scheduled
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setScheduling(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="secondary" size="sm" onClick={() => setScheduling(true)}>
                <Truck size={12} /> Pickup is scheduled…
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                title={
                  g.recentAudit
                    ? `Uses audit ${g.recentAudit.doc_number ?? g.recentAudit.id} from ${new Date(g.recentAudit.created_at).toLocaleDateString()}`
                    : 'Requires a cylinder audit in the last 30 days'
                }
                onClick={() => run({ action: 'resolve_tanks', resolution: 'closure_audit' })}
              >
                <CheckCircle2 size={12} /> Closure tank audit performed
              </Button>
              <Link to={subject.customerId ? '/audit' : `/audit/qbo/${subject.qboId}`} className="text-xs text-brix-primary font-semibold no-underline">
                Open the driver audit app →
              </Link>
            </div>
          )}
          {!g.recentAudit && (
            <p className="text-[11px] text-brix-muted">
              No cylinder audit on file in the last 30 days — perform one at{' '}
              <span className="font-mono">{subject.customerId ? '/audit' : `/audit/qbo/${subject.qboId}`}</span> to use
              the audit option.
            </p>
          )}
        </div>
      ) : null}
    </Step>
  );
}

/* ------------------------------ step 3: balances ------------------------------ */

function BalancesStep({
  gates,
  closure,
  busy,
  run,
}: {
  gates: ClosureGates;
  closure: ClosureRow;
  busy: boolean;
  run: RunFn;
}) {
  const [writing, setWriting] = useState(false);
  const [note, setNote] = useState('');
  const g = gates.balances;

  return (
    <Step n={3} title="Open balances cleared or written off" ok={g.ok} icon={<Banknote size={13} />}>
      {g.total <= 0 ? (
        <p className="text-sm text-brix-muted">No open balance — nothing owed.</p>
      ) : (
        <p className="text-sm text-brix-ink">
          Open balance of <strong>{money(g.total)}</strong> across {g.count} invoice
          {g.count === 1 ? '' : 's'}. Collect payment or write it off in QuickBooks, then record it
          here.
        </p>
      )}
      {g.writtenOff ? (
        <p className="text-xs text-emerald-700">
          Recorded by {closure.balance_resolved_by} — {closure.balance_writeoff_note}
        </p>
      ) : g.total > 0 ? (
        writing ? (
          <div className="space-y-2">
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="How it was resolved (e.g. written off via credit memo CM-1042 in QBO)"
            />
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={busy || note.trim().length < 5}
                onClick={() => run({ action: 'writeoff_balance', note: note.trim() }, () => setWriting(false))}
              >
                Record cleared / written off
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setWriting(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => setWriting(true)}>
            Balance was cleared or written off…
          </Button>
        )
      ) : null}
    </Step>
  );
}

/* ------------------------------ step 4: refunds ------------------------------ */

interface ReturnDraftLine {
  name: string;
  display_name: string;
  qbo_item_id: string;
  qty: number;
  unit_price: number;
}

function RefundsStep({
  gates,
  closure,
  busy,
  run,
}: {
  gates: ClosureGates;
  closure: ClosureRow;
  busy: boolean;
  run: RunFn;
}) {
  const [returning, setReturning] = useState(false);
  const g = gates.refunds;

  return (
    <Step n={4} title="Customer refunds" ok={g.ok} icon={<Undo2 size={13} />}>
      {g.ok ? (
        g.answer === 'none' ? (
          <p className="text-xs text-emerald-700">
            No refund requests{closure.refund_note ? ` — ${closure.refund_note}` : ''} (recorded by{' '}
            {closure.refunds_resolved_by}).
          </p>
        ) : (
          <p className="text-xs text-emerald-700">
            Return order booked — QuickBooks credit memo {g.creditMemoDoc ?? `#${g.creditMemoId}`}
            {closure.refund_total != null ? ` for ${money(Number(closure.refund_total))}` : ''}
            {g.sfJobId
              ? ` · SF pickup job #${g.sfJobId}`
              : ' · SF pickup job was not created — dispatch the pickup in Service Fusion manually'}
            .
          </p>
        )
      ) : (
        <>
          <p className="text-sm text-brix-ink">
            Does the customer have any refund requests (deposits, returned product, overpayment)?
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => run({ action: 'resolve_refunds', answer: 'none' })}
            >
              No refunds owed
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setReturning(true)}>
              <Undo2 size={12} /> Yes — create a return order
            </Button>
          </div>
        </>
      )}
      {returning && !g.ok && (
        <ReturnOrderForm
          busy={busy}
          onCancel={() => setReturning(false)}
          onSubmit={(lines, note) =>
            run(
              {
                action: 'return_order',
                lines: lines.map((l) => ({
                  name: l.name,
                  display_name: l.display_name || undefined,
                  qbo_item_id: l.qbo_item_id,
                  qty: l.qty,
                  unit_price: l.unit_price,
                })),
                note: note || undefined,
              },
              () => setReturning(false),
            )
          }
        />
      )}
    </Step>
  );
}

/** Return-order builder: catalog-picked lines pushed to SF with negative qty. */
function ReturnOrderForm({
  busy,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  onCancel: () => void;
  onSubmit: (lines: ReturnDraftLine[], note: string) => void;
}) {
  const [lines, setLines] = useState<ReturnDraftLine[]>([]);
  const [note, setNote] = useState('');
  const [q, setQ] = useState('');
  const [results, setResults] = useState<CatalogPickerItem[]>([]);
  const [searching, setSearching] = useState(false);

  async function doSearch() {
    if (q.trim().length < 2) return;
    setSearching(true);
    try {
      setResults(await searchClosureCatalog(q.trim()));
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }

  const total = lines.reduce((s, l) => s + l.qty * l.unit_price, 0);

  return (
    <div className="rounded-lg border border-brix-primary/25 bg-brix-primary-tint/20 px-4 py-3 space-y-3">
      <div className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
        Return order — pushes to Service Fusion as a negative order
      </div>

      {/* Item search */}
      <div className="flex gap-2">
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void doSearch();
            }
          }}
          placeholder="Search the catalog (name or SKU)…"
        />
        <Button variant="secondary" size="sm" onClick={() => void doSearch()} disabled={searching}>
          {searching ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
        </Button>
      </div>
      {results.length > 0 && (
        <div className="space-y-1">
          {results.map((r) => (
            <button
              key={r.id}
              type="button"
              className="w-full text-left rounded-md border border-brix-border bg-brix-surface px-2.5 py-1.5 text-sm text-brix-ink hover:bg-brix-primary-tint/40 cursor-pointer flex items-center gap-2"
              onClick={() => {
                setLines((ls) => [
                  ...ls,
                  {
                    name: r.name,
                    display_name: r.display_name ?? '',
                    qbo_item_id: r.qbo_item_id,
                    qty: 1,
                    unit_price: Number(r.list_price ?? 0),
                  },
                ]);
                setResults([]);
                setQ('');
              }}
            >
              <Plus size={12} className="text-brix-primary shrink-0" />
              <span className="truncate flex-1">{r.display_name || r.name}</span>
              <span className="text-[11px] text-brix-muted font-mono">{r.sku ?? ''}</span>
              <span className="text-[11px] text-brix-muted">{money(Number(r.list_price ?? 0))}</span>
            </button>
          ))}
        </div>
      )}

      {/* Lines */}
      {lines.length > 0 && (
        <div className="space-y-1.5">
          {lines.map((l, i) => (
            <div key={i} className="flex items-center gap-2 rounded-md border border-brix-border bg-brix-surface px-2.5 py-1.5">
              <span className="text-sm text-brix-ink truncate flex-1">{l.display_name || l.name}</span>
              <label className="text-[11px] text-brix-muted">Qty</label>
              <input
                type="number"
                min={1}
                value={l.qty}
                onChange={(e) =>
                  setLines((ls) => ls.map((row, idx) => (idx === i ? { ...row, qty: Math.max(1, Number(e.target.value) || 1) } : row)))
                }
                className="w-16 rounded border border-brix-border bg-brix-surface px-2 py-1 text-sm text-brix-ink"
              />
              <label className="text-[11px] text-brix-muted">$/unit</label>
              <input
                type="number"
                min={0}
                step="0.01"
                value={l.unit_price}
                onChange={(e) =>
                  setLines((ls) => ls.map((row, idx) => (idx === i ? { ...row, unit_price: Math.max(0, Number(e.target.value) || 0) } : row)))
                }
                className="w-24 rounded border border-brix-border bg-brix-surface px-2 py-1 text-sm text-brix-ink"
              />
              <button
                type="button"
                className="text-brix-muted hover:text-brix-ink cursor-pointer"
                onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))}
                aria-label="Remove line"
              >
                <X size={14} />
              </button>
            </div>
          ))}
          <div className="text-right text-sm text-brix-ink font-semibold">
            Refund total: {money(total)}
          </div>
        </div>
      )}

      <Input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Reason / reference (optional — shows on the SF job)"
      />

      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          disabled={busy || lines.length === 0}
          onClick={() => onSubmit(lines, note.trim())}
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />}
          Push return order to Service Fusion
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      <p className="text-[11px] text-brix-muted">
        Books the credit in QuickBooks as a credit memo (the "negative order" — Service Fusion
        can't carry negative amounts) and dispatches a $0 SF pickup job listing the returned
        items, so the return flows through operations like a regular order.
      </p>
    </div>
  );
}
